import * as React from 'react'

import { Icon } from '../icons/Icon'
import { Stack } from '../layout/Stack'

type TIconProps = Omit<
  PropsOf<typeof Icon>,
  'minWidth' | 'minHeight' | 'contrast' | 'color' | 'children'
>

type TProps = Children &
  TIconProps & {
    /**
     * key of projects colors, for deeper values use dot notation: `specialColors.company`
     */
    iconColor?: PropsOf<typeof Icon>['color']
    /**
     * how contrasted the icon should be on said background
     */
    iconContrast?: PropsOf<typeof Icon>['contrast']
    /**
     * CSS value with unit (`3rem`)
     * use eg. to align multiple icons that required different sizes under each other
     */
    iconPartForcedWidth?: PropsOf<typeof Icon>['minWidth']
    /**
     * CSS value with unit (`3rem`)
     * use eg. to align multiple icons that required different sizes under each other
     * or for "align-with-first-text-line" cases
     */
    iconPartForcedHeight?: PropsOf<typeof Icon>['minHeight']
    /**
     * vertical icon alignment
     */
    iconAlign?: PropsOf<typeof Stack>['align']
    /**
     * key of project spaces – sets the space between the icon and the content
     */
    spacing?: PropsOf<typeof Stack>['spacing']
  }

/**
 * Icon next to some content (in children)
 */
export const IconLine: React.FC<TProps> = ({
  children,
  iconColor,
  iconContrast,
  spacing = `medium`,
  iconSize = `1.5rem`,
  iconPartForcedWidth,
  iconPartForcedHeight,
  iconAlign = `center`,
  ...iconProps
}) => {
  return (
    <Stack direction={`row`} spacing={spacing} align={iconAlign}>
      <Icon
        {...iconProps}
        color={iconColor}
        iconSize={iconSize}
        contrast={iconContrast}
        minWidth={iconPartForcedWidth}
        minHeight={iconPartForcedHeight}
      />
      {children}
    </Stack>
  )
}
